/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { MapContainer, TileLayer } from 'react-leaflet';
import { WidgetProps } from '@rjsf/utils';

import { LatLngBounds, Map } from 'leaflet';
import { Button } from 'primereact/button';
import { BBOXUtils } from './utils.ts';
import { BBoxMapControler } from './BBoxMapControler.tsx';

const DEFAULT_OGC_BOUND = [0.74707, 43.101, 2.11487, 44.0955];

export const BBoxEditableWidget = (props: WidgetProps): React.JSX.Element => {
  const mapRef = useRef<Map>(null);

  const [formBBox, setFormBBox] = useState<LatLngBounds | undefined>();

  const { value, schema } = props;
  useEffect(() => {
    if (BBOXUtils.isValidBBox(value)) {
      const initalFormBbox = BBOXUtils.arrayToLatLngBounds(value);
      setFormBBox(initalFormBbox);
    } else {
      setFormBBox(BBOXUtils.arrayToLatLngBounds(DEFAULT_OGC_BOUND));
    }
  }, [value]);

  const mapIsVisible = useMemo(() => {
    return schema.minItems > 0 && formBBox !== undefined;
  }, [schema, formBBox]);

  return (
    <div className={'w-full flex flex-column'}>
      <div className="flex w-full align-items-center justify-content-between mb-3">
        <label className="ms-Label capitalize font-semibold text-sm">{schema.title}</label>

        {schema.minItems === 0 && (
          <>
            {mapIsVisible ? (
              <Button
                style={{ borderRadius: 0, height: 30, width: 30 }}
                onClick={() => {
                  props.onChange([]);
                }}
                type="button"
                size="small"
                outlined
                icon="pi pi-trash"
                severity="contrast"
              />
            ) : (
              <Button
                style={{ borderRadius: 0, height: 30, width: 30 }}
                onClick={() => {
                  props.onChange(DEFAULT_OGC_BOUND);
                }}
                type="button"
                size="small"
                outlined
                icon="pi pi-plus"
                severity="contrast"
              />
            )}
          </>
        )}
      </div>

      {mapIsVisible && formBBox && (
        <>
          <MapContainer
            ref={mapRef}
            id={'map'}
            center={formBBox.getCenter()}
            style={{ height: '20rem', minWidth: '20rem', width: '100%' }}
            zoom={8}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <BBoxMapControler
              defaultBound={formBBox}
              setBBox={(bbox) => {
                props.onChange(bbox);
              }}
            />
          </MapContainer>
          {Array.isArray(value) && value.length > 0 && (
            <small className="text-right" style={{ marginTop: '0.5rem' }}>
              <span className="font-light">Value</span> : {JSON.stringify(value)}
            </small>
          )}
        </>
      )}
    </div>
  );
};
